import { useEffect, useRef, useState } from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';

import { SCOPES } from '../../../lib/scopes';
import { ToastManager } from '../../../ToastManager';
import { DeviceUpdatesChangelogDialog } from '../../settings/system/device-updates/DeviceUpdatesChangelogDialog.tsx';
import { DeviceUpdatesPromptDialog } from '../../settings/system/device-updates/DeviceUpdatesPromptDialog.tsx';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useDevice } from '../../../store/devices/useDevices.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';

import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';
import styles from './DeviceSettingsMaintenanceFirmwareUpdate.module.css';

import { MonoButton } from '../../../components/buttons/MonoButton';
import { SpinningIcon } from '../../../components/common/SpinningIcon';
import { Heading3Bold } from '../../../components/common/text/Heading3Bold';
import { TextSmall } from '../../../components/common/text/TextSmall';

import { DeviceSettingsFirmwareUpdateWakeDialog } from './DeviceSettingsFirmwareUpdateWakeDialog';

const IN_PROGRESS_FIRMWARE_UPDATE_STATES = new Set(['queued', 'installing', 'aborting']);

export function DeviceSettingsMaintenanceFirmwareUpdate(props) {
  const { i18n } = useI18n();
  const { api, scopes } = useApi();
  const { deviceInstance } = useDevice({ deviceId: props.device.id });
  const firmwareUpdate = props.device.firmwareUpdate;
  const [progress, setProgress] = useState(firmwareUpdate?.progress ?? null);
  const [isCheckingForUpdates, setIsCheckingForUpdates] = useState(false);
  const [isStartingUpdate, setIsStartingUpdate] = useState(false);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [isWakeDialogOpen, setIsWakeDialogOpen] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [wakeInstruction, setWakeInstruction] = useState(null);

  const state = firmwareUpdate?.state ?? 'idle';
  const currentVersion = getString(firmwareUpdate?.currentVersion);
  const availableVersion = getString(firmwareUpdate?.availableVersion);
  const isInProgress = IN_PROGRESS_FIRMWARE_UPDATE_STATES.has(state);
  const canReadUpdates = scopes[SCOPES.UPDATES_READONLY] === true;
  const canManageUpdates = scopes[SCOPES.UPDATES] === true;
  const isBusy =
    isCheckingForUpdates === true || isStartingUpdate === true || isInProgress === true;

  useEffect(() => {
    setProgress(firmwareUpdate?.progress ?? null);
  }, [props.device.id, firmwareUpdate?.progress]);

  useEffect(() => {
    if (deviceInstance == null) {
      return;
    }

    function handleFirmwareUpdateState(update) {
      if ('progress' in update) {
        setProgress(update.progress ?? null);
      }
    }

    // Progress-only events do not update the device snapshot.
    deviceInstance.on('firmware-update-state', handleFirmwareUpdateState);
    const leasePromise = deviceInstance.acquireConnection().catch((error) => {
      ToastManager.handleError(error);
      return null;
    });

    return function cleanup() {
      deviceInstance.off('firmware-update-state', handleFirmwareUpdateState);
      leasePromise
        .then(async (lease) => {
          await lease?.release();
        })
        .catch(ToastManager.handleError);
    };
  }, [deviceInstance]);

  function checkDeviceReady() {
    const device = useDevice.store.get().byId?.[props.device.id];

    if (device?.ready !== true || device.available === false) {
      ToastManager.addError({
        message: i18n.messageFormatter('settings.system.deviceUpdatesDeviceNotReady'),
      });
      return false;
    }

    return true;
  }

  async function handleCheckForUpdates() {
    if (api == null || canManageUpdates === false || isBusy === true) {
      return;
    }

    setIsCheckingForUpdates(true);

    try {
      const result = await api.call({
        method: 'POST',
        path: `/api/manager/devices/device/${props.device.id}/firmware/check`,
        body: {},
        $timeout: 30000,
      });

      setWakeInstruction(getString(result?.wakeInstruction));

      if (getString(result?.availableVersion) == null) {
        ToastManager.add({
          type: ToastManager.TYPES.SUCCESS,
          icon: glyph.core.checkmarkCircleFill,
          iconColor: theme.color.white,
          message: i18n.messageFormatter('settings.system.deviceUpdatesDeviceUpToDate', {
            version: getString(result?.currentVersion) ?? currentVersion ?? '-',
          }),
        });
      }
    } catch (error) {
      ToastManager.handleError(error);
    } finally {
      setIsCheckingForUpdates(false);
    }
  }

  async function handleStartUpdate() {
    if (
      api == null ||
      canManageUpdates === false ||
      isStartingUpdate === true ||
      availableVersion == null
    ) {
      return;
    }

    if (checkDeviceReady() === false) {
      return;
    }

    setIsStartingUpdate(true);

    try {
      await api.call({
        method: 'POST',
        path: `/api/manager/devices/device/${props.device.id}/firmware/start`,
        body: { version: availableVersion },
        $timeout: 30000,
      });

      setIsPromptOpen(false);
      setIsWakeDialogOpen(false);
    } catch (error) {
      ToastManager.handleError(error);
    } finally {
      setIsStartingUpdate(false);
    }
  }

  async function loadWakeInstruction() {
    if (api == null || canReadUpdates === false || availableVersion == null) {
      return null;
    }

    try {
      const params = new URLSearchParams({ version: availableVersion });
      const result = await api.call({
        method: 'GET',
        path: `/api/manager/devices/device/${props.device.id}/firmware/details?${params.toString()}`,
        $timeout: 30000,
      });

      return getString(result?.wakeInstruction);
    } catch (error) {
      ToastManager.handleError(error);
      return null;
    }
  }

  async function handleActionPress() {
    if (state === 'available' || (state === 'error' && availableVersion != null)) {
      if (checkDeviceReady() === false) {
        return;
      }

      setIsPromptOpen(true);
      return;
    }

    await handleCheckForUpdates();
  }

  async function handlePromptStartUpdate() {
    if (checkDeviceReady() === false) {
      return;
    }

    if (firmwareUpdate?.requiresWake === true) {
      if (wakeInstruction == null) {
        const nextWakeInstruction = await loadWakeInstruction();
        setWakeInstruction(nextWakeInstruction);
      }

      setIsPromptOpen(false);
      setIsWakeDialogOpen(true);
      return;
    }

    await handleStartUpdate();
  }

  const actionLabel = getActionLabel({
    i18n,
    isCheckingForUpdates,
    message: getString(firmwareUpdate?.message),
    progress,
    state,
  });
  const showsSpinner =
    isCheckingForUpdates === true || isStartingUpdate === true || isInProgress === true;
  const description = getDescription({
    availableVersion,
    canOpenChangelog: canReadUpdates === true && availableVersion != null,
    currentVersion,
    firmwareUpdate,
    i18n,
    onChangelogPress: () => {
      setIsChangelogOpen(true);
    },
    state,
  });

  return (
    <div className={styles.root}>
      <Heading3Bold className={styles.heading}>
        {i18n.messageFormatter('settings.system.deviceUpdates')}
      </Heading3Bold>

      <MonoButton
        styleWidth="full"
        styleFlat={true}
        styleColor="blue"
        onPress={handleActionPress}
        isDisabled={isBusy === true || canManageUpdates === false}
      >
        <span className={styles.actionContent}>
          {showsSpinner === true && (
            <SpinningIcon
              source={glyph.core.spinner}
              color={theme.color.blue}
              size={theme.icon.size_medium}
            />
          )}
          <span className={styles.actionLabel}>{actionLabel}</span>
        </span>
      </MonoButton>

      {description != null && (
        <TextSmall
          className={styles.description}
          color={state === 'error' ? theme.color.red : theme.color.text_body_light}
        >
          {description}
        </TextSmall>
      )}

      {isPromptOpen === true && (
        <DeviceUpdatesPromptDialog
          onClose={() => {
            if (isStartingUpdate === true) {
              return;
            }

            setIsPromptOpen(false);
          }}
          onStartUpdate={handlePromptStartUpdate}
          isStartingUpdate={isStartingUpdate}
        />
      )}

      {isWakeDialogOpen === true && (
        <DeviceSettingsFirmwareUpdateWakeDialog
          onClose={() => {
            if (isStartingUpdate === true) {
              return;
            }

            setIsWakeDialogOpen(false);
          }}
          onConfirm={handleStartUpdate}
          isStartingUpdate={isStartingUpdate}
          wakeInstruction={wakeInstruction}
        />
      )}

      {isChangelogOpen === true && availableVersion != null && (
        <DeviceUpdatesChangelogDialog
          availableVersion={availableVersion}
          currentVersion={currentVersion ?? '-'}
          deviceId={props.device.id}
          onClose={() => {
            setIsChangelogOpen(false);
          }}
        />
      )}
    </div>
  );
}

function DeviceSettingsFirmwareUpdateVersionButton(props) {
  const ref = useRef(null);
  const button = useButton(props, ref);
  const focusRing = useFocusRing();

  return (
    <button
      {...mergeProps(button.buttonProps, focusRing.focusProps)}
      className={styles.versionButton}
      data-is-focus-visible={focusRing.isFocusVisible}
      ref={ref}
      type="button"
    >
      {props.children}
    </button>
  );
}

function getActionLabel({ i18n, isCheckingForUpdates, message, progress, state }) {
  if (isCheckingForUpdates === true) {
    return i18n.messageFormatter('settings.system.deviceUpdatesCheckingForUpdates');
  }

  if (IN_PROGRESS_FIRMWARE_UPDATE_STATES.has(state) && message != null) {
    if (progress != null) {
      return `${Math.round(progress)}% • ${message}`;
    }

    return message;
  }

  switch (state) {
    case 'available':
      return i18n.messageFormatter('settings.system.deviceUpdatesActionUpdate');

    case 'queued':
      return i18n.messageFormatter('settings.system.deviceUpdatesStateQueued');

    case 'installing': {
      const label = i18n.messageFormatter('settings.system.deviceUpdatesStateInstallingFirmware');

      if (progress != null) {
        return `${Math.round(progress)}% • ${label}`;
      }

      return label;
    }

    case 'aborting':
      return i18n.messageFormatter('settings.system.deviceUpdatesStateAbortingUpdate');

    case 'error':
      return i18n.messageFormatter('settings.system.deviceUpdatesActionRetry');

    default:
      return i18n.messageFormatter('settings.system.deviceUpdatesActionCheck');
  }
}

function getDescription({
  availableVersion,
  canOpenChangelog,
  currentVersion,
  firmwareUpdate,
  i18n,
  onChangelogPress,
  state,
}) {
  if (state === 'error') {
    const message = getString(firmwareUpdate?.message);

    return message ?? i18n.messageFormatter('settings.system.deviceUpdatesNoMessage');
  }

  if (availableVersion != null) {
    return (
      <>
        {state === 'available' && (
          <>{i18n.messageFormatter('settings.system.deviceUpdatesFirmwareUpdateAvailable')} </>
        )}
        {currentVersion != null && (
          <>
            {i18n.messageFormatter('settings.system.deviceUpdatesInstalledVersion', {
              version: currentVersion,
            })}{' '}
          </>
        )}
        {i18n.messageFormatter('settings.system.deviceUpdatesAvailableVersionLabel')}:{' '}
        {canOpenChangelog === true ? (
          <DeviceSettingsFirmwareUpdateVersionButton onPress={onChangelogPress}>
            {availableVersion}
          </DeviceSettingsFirmwareUpdateVersionButton>
        ) : (
          availableVersion
        )}
        .
      </>
    );
  }

  if (currentVersion != null) {
    return `${i18n.messageFormatter(
      'settings.system.deviceUpdatesCurrentVersionLabel',
    )}: ${currentVersion}`;
  }

  return null;
}

function getString(value) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}
